<template>
  <div>
    <Head :title="'Paciente: ' + patient.user.name" />

    <div
      class="w-full"
      :class="{ 'lg:flex flex-row-reverse gap-4': appointmentOpen }"
    >
      <appointment-video
        ref="appointmentVideo"
        :class="{ hidden: !appointmentOpen }"
      />

      <div class="shadow_box w-full rounded-lg p-6">
        <div class="flex w-full">
          <div
            v-if="patient.user?.photo?.url"
            class="pr-4 w-full flex justify-center lg:w-[150px]"
          >
            <img :src="patient.user.photo.url" class="max-w-32 max-h-32" />
          </div>
          <div class="lg:pr-6 mt-6 w-full">
            <h1 class="mb-2 text-xl text-gray-777 font-roboto">
              Paciente: {{ patient.user.name }}
            </h1>
            <div class="text-gray-777">{{ patient.cpf }}</div>
          </div>
          <div v-if="patient.b2b" class="pr-2">
            <button
              class="flex justify-center items-end font-roboto font-medium btn-primary uppercase w-full mt-4 ml-0 lg:ml-2 lg:mt-0"
              @click="openModalConfig()"
            >
              <font-awesome-icon
                icon="fa-plus"
                class="text-white text-lg mr-2"
              /><span class="">Liberar Especialidade</span>
            </button>
          </div>
        </div>

        <hr class="my-5" />
        <tabs
          :key="appointmentOpen"
          :selected-item-index="selectedItemIndex"
          :items="tabItems"
          item-class="px-2"
        >
          <template #content.online-consultation>
            <online-consultation />
          </template>

          <template #content.timeline>
            <timeline />
          </template>

          <template #content.diagnosis>
            <diagnosis />
          </template>

          <template #title.appointments>
            Atendimentos
            <div v-if="appointmentOpen" class="inline-block ml-1">
              <span class="flex h-2 w-2 relative">
                <span
                  class="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-secondary opacity-75"
                />
                <span
                  class="relative rounded-full h-2 w-2 bg-primary text-white text-center"
                />
              </span>
            </div>
          </template>

          <template #content.appointments>
            <appointments ref="appointments" />
          </template>

          <template #content.vital-data>
            <vital-data />
          </template>

          <template #content.prescription>
            <prescription :token="token" />
          </template>

          <template #content.notifications>
            <notifications />
          </template>
        </tabs>
      </div>
    </div>

    <modal
      id="modal-config"
      ref="modalConfig"
      size="modal-lg"
      :modal-scrollable="true"
      :modal-backdrop="true"
    >
      <template #header>
        <div class="text-gray-777 font-roboto uppercase">
          <span>Liberar Especialidade</span>
        </div>
      </template>
      <template #body>
        <div>
          <div style="display: inline-flex; align-items: flex-end">
            <select-input
              v-model="formConfig.specialty_id"
              :error="formConfig.errors.specialty_id"
              class="lg:pr-6 mt-6 w-full"
              placeholder="Especialidade"
              label="Especialidade do atendimento"
            >
              <option :value="null" disabled class="hidden invisible">
                Especialidade do atendimento
              </option>
              <option
                v-for="(value, name, index) in formConfig.specialties_options"
                :key="value.name"
                :value="value.id"
              >
                {{ value.name }}
              </option>
            </select-input>
          </div>
        </div>
      </template>
      <template #footer>
        <button
          type="button"
          class="px-6 py-2.5 bg-gray-600 text-white font-roboto font-medium text-xs leading-tight uppercase rounded shadow-md hover:bg-gray-700 hover:shadow-lg focus:bg-gray-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-gray-800 active:shadow-lg transition duration-150 ease-in-out"
          @click.prevent="closeModalConfig"
        >
          Cancelar
        </button>
        <loading-button
          :loading="loadModalConfig"
          class="btn-primary px-6 py-2.5 text-xs leading-tight uppercase ml-1 cursor-pointer"
          type="submit"
          @click.prevent="saveModalConfig"
        >
          Liberar
        </loading-button>
      </template>
    </modal>
  </div>
</template>

<script>
import { computed } from 'vue'
import { Head, usePage, useForm } from '@inertiajs/inertia-vue3'

import Layout from '@/Shared/Layout/Layout'
import Tabs from '@/Shared/Components/Tabs/Tabs'
import Timeline from '@/Pages/Dashboard/Patients/Tabs/Timeline'
import VitalData from '@/Pages/Dashboard/Patients/Tabs/VitalData'
import Modal from '@/Shared/Modal/Modal'
// import Prescription from '@/Pages/Dashboard/Patients/Tabs/Prescription'
import Certificate from '@/Pages/Dashboard/Patients/Tabs/Certificate'
import Diagnosis from '@/Pages/Dashboard/Patients/Tabs/Diagnosis'
import Document from '@/Pages/Dashboard/Patients/Tabs/Document'
import OnlineConsultation from '@/Pages/Dashboard/Patients/Tabs/OnlineConsultation'
import Notifications from '@/Pages/Dashboard/Patients/Tabs/Notifications'
import Appointments from '@/Pages/Dashboard/Patients/Tabs/Appointments'
import AppointmentVideo from '@/Pages/Dashboard/Patients/Tabs/AppointmentVideo'

import Prescription from './Tabs/PrescriptionMemed'
import SelectInput from '@/Shared/Fields/SelectInput'
import LoadingButton from '@/Shared/Components/LoadingButton.vue'

export default {
  components: {
    Head,
    Tabs,
    Timeline,
    VitalData,
    Prescription,
    Certificate,
    Diagnosis,
    Document,
    OnlineConsultation,
    Notifications,
    Appointments,
    AppointmentVideo,
    Modal,
    LoadingButton,
    SelectInput,
  },
  inject: ['collapseMenu', 'stopInactivityMessage', 'startInactivityMessage'],
  provide() {
    return {
      doctorStatus: this.$props.status,
      memedjavascript: this.$props.memedjavascript,
      ahlifeAddress: this.$props.ahlifeAddress,
      memedapiurl: this.memedApiUrl,
      loadOptions: this.loadOptions,
      openAppointment: this.openAppointment,
      closeAppointment: this.closeAppointment,
      openAppointmentSide: this.openAppointmentSide,
      closeAppointmentSide: this.closeAppointmentSide,
      patient: this.patient,
      isLoadMemed: computed(() => this.loadMemed),
      appointmentOpen: () => computed(() => this.appointmentOpen).value,
      professionals: () => computed(() => this.professionals).value,
      professional_addresses: () =>
        computed(() => this.professional_addresses).value,
      goals: () => computed(() => this.goals).value,
      certificate_types: () => computed(() => this.certificate_types).value,
      certificate_cids: () => computed(() => this.certificate_cids).value,
      prescription_pharmaceutical_forms: () =>
        computed(() => this.prescription_pharmaceutical_forms).value,
      prescription_routes_administration: () =>
        computed(() => this.prescription_routes_administration).value,
      prescription_frequencies: () =>
        computed(() => this.prescription_frequencies).value,
      prescription_frequency_types: () =>
        computed(() => this.prescription_frequency_types).value,
      prescription_shifts: () => computed(() => this.prescription_shifts).value,
      vital_datas: () => computed(() => this.vital_datas).value,
      timelines: () => computed(() => this.timelines).value,
      appointments: () => computed(() => this.appointments).value,
      prescription_types: () => computed(() => this.prescription_types).value,
      states_options: () => computed(() => this.states_options).value,
    }
  },
  layout: Layout,
  props: {
    ahlifeAddress: Object,
    patient: Object,
    appointment: Object,
    token: String,
    status: String,
    memedjavascript: String,
    memedApiUrl: String,
  },
  setup() {
    const user = computed(() => usePage().props.value.auth.user)
    return { user }
  },

  data() {
    return {
      loadMemed: true,
      appointmentOpen: false,
      professionals: Object,
      professional_addresses: Object,
      goals: Object,
      certificate_types: Object,
      certificate_cids: Object,
      prescription_pharmaceutical_forms: Object,
      prescription_routes_administration: Object,
      prescription_frequencies: Object,
      prescription_frequency_types: Object,
      prescription_shifts: Object,
      vital_datas: Object,
      timelines: Object,
      appointments: Object,
      prescription_types: Object,
      states_options: Object,
      tabItems: [
        {
          idTab: 'online-consultation',
          title: 'Consulta Online',
        },
        {
          idTab: 'timeline',
          title: 'Histórico',
        },
        {
          idTab: 'appointments',
          title: 'Atendimentos',
        },
        {
          idTab: 'vital-data',
          title: 'Dados Vitais',
        },
        {
          idTab: 'prescription',
          title: 'Prescrição',
        },
        {
          idTab: 'diagnosis',
          title: 'Diagnóstico',
        },
        {
          idTab: 'notifications',
          title: 'Notificações',
        },
      ],
    }
  },
  computed: {
    selectedItemIndex: function () {
      if (this.appointmentOpen) {
        return 0
      }
      return this.appointment ? 2 : 0
    },
  },
  mounted() {
    this.setupMemed()
    this.loadOptions()

    if (this.appointment) {
      setTimeout(() => {
        this.openAppointmentSide(this.appointment)
      }, 1000)
    }
  },

  unmounted() {
    this.startInactivityMessage()
    this.handleRemoveListener()
  },
  methods: {
    async setupMemed() {
      const hasScriptTag = document.getElementById('memed-module-load')

      if (hasScriptTag) {
        this.loadMemed = false
        return
      }

      this.loadMemed = true
      const script = document.createElement('script')

      script.type = 'text/javascript'
      script.dataset.color = '#576cff'

      script.setAttribute('id', 'memed-module-load')
      script.dataset.token = this.$props.token
      script.src = this.memedjavascript

      script.addEventListener('load', this.handleLoadMemed)
      document.body.appendChild(script)
    },

    handleRemoveListener() {
      const script = document.getElementById('memed-module-load')

      if (!script)
        return

      script.removeEventListener('load', this.handleLoadMemed)
    },

    handleLoadMemed() {
      const professionalAddress = this.$props.ahlifeAddress

      window.MdSinapsePrescricao.event.add('core:moduleInit', (module) => {
        if (module.name === 'plataforma.prescricao') {
          window.MdHub.command.send('plataforma.prescricao', 'setWorkplace', {
            state: professionalAddress.state,
            address: `${professionalAddress.street}, ${professionalAddress.number}, ${professionalAddress.complement}`,
          })

          window.MdHub.command.send(
            'plataforma.prescricao',
            'setFeatureToggle',
            {
              guidesOnboarding: false,
              historyPrescription: false,
              deletePatient: false,
              removePatient: false,
              editPatient: false,
              dropdownSync: false,
            },
          )

          this.loadMemed = false
        }
      })
    },

    async loadOptions() {
      await fetch('/patients/get-options-detail/' + this.patient.id).then(
        (res) => {
          res.json().then((json) => {
            this.professionals = json.professionals
            this.professional_addresses = json.professional_addresses
            this.goals = json.goals
            this.certificate_types = json.certificate_types
            this.certificate_cids = json.certificate_cids
            this.prescription_pharmaceutical_forms =
              json.prescription_pharmaceutical_forms
            this.prescription_routes_administration =
              json.prescription_routes_administration
            this.prescription_frequencies = json.prescription_frequencies
            this.prescription_frequency_types =
              json.prescription_frequency_types
            this.prescription_shifts = json.prescription_shifts
            this.vital_datas = json.vital_datas
            this.timelines = json.timelines
            this.appointments = json.appointments
            this.prescription_types = json.prescription_types
            this.states_options = json.states_options
            this.specialties_options = json.specialties_options
          })
        },
      )
    },
    openAppointment() {
      this.stopInactivityMessage()

      this.appointmentOpen = true
    },
    closeAppointment() {
      this.startInactivityMessage()

      this.appointmentOpen = false
    },

    openAppointmentSide(appointment) {
      this.$refs.appointmentVideo.openRoom(appointment)
      this.collapseMenu()
      this.appointmentOpen = true
      setTimeout(() => this.$refs.appointments.reloadTable(), 5000)
    },
    closeAppointmentSide() {
      this.closeAppointment()
      setTimeout(() => this.$refs.appointments.reloadTable(), 500)
    },
    openModalConfig() {
      this.formConfig = useForm({
        specialties_options: this.specialties_options,
        specialty_id: '',
      })
      this.$refs.modalConfig.showModal = true
    },
    saveModalConfig() {
      this.formConfig.post(
        `/patients/save-request-appointment/${this.patient.id}`,
        {
          onFinish: () => {
            if (!Object.keys(this.formConfig.errors).length) {
              this.closeModalConfig()
            } else {
              this.loadModalConfig = false
            }
          },
        },
      )
    },
    closeModalConfig() {
      this.$refs.modalConfig.showModal = false
    },
  },
}
</script>
